import React, {Suspense, lazy, useCallback, useEffect, useMemo, useState} from "react";
import {Badge, Box, Button, Flex, HStack, Icon, Text, VStack} from "@chakra-ui/react";
import {keyframes} from "@emotion/react";
import {FaBell, FaRegClock} from "react-icons/fa";
import {FiArrowRight} from "react-icons/fi";
import {typeBadgeMap} from "../../../../utils/BadgeUtils";
import {BELL_INFO_UPDATED_EVENT, readBellInfo} from "../../../../utils/BellStorageUtils";
import {
    HERO_ANIMATION_UPDATED_EVENT,
    getHeroGifForTime,
    isHeroAnimationEnabled,
} from "../../../../utils/HeroAnimationConfig";

// Lazy-loaded so the marquee bundle (and its /bell/marquee fetch) only loads
// once the hero renders. Comment out the <MarqueeSlider/> usage below to hide.
const MarqueeSlider = lazy(() => import("./MarqueeSlider"));

const pulse = keyframes`
    0% { transform: scale(1); opacity: 0.9; }
    50% { transform: scale(1.12); opacity: 0.45; }
    100% { transform: scale(1); opacity: 0.9; }
`;

// Bell swing while playing
const ring = keyframes`
    0% { transform: rotate(0); }
    15% { transform: rotate(20deg); }
    30% { transform: rotate(-16deg); }
    45% { transform: rotate(12deg); }
    60% { transform: rotate(-8deg); }
    75% { transform: rotate(4deg); }
    100% { transform: rotate(0); }
`;

// Expanding sound waves around the bell
const wave = keyframes`
    0% { transform: scale(0.7); opacity: 0.7; }
    100% { transform: scale(2.4); opacity: 0; }
`;

// Equalizer bars
const bounce = keyframes`
    0%, 100% { transform: scaleY(0.35); }
    50% { transform: scaleY(1); }
`;

// "10:00 AM" -> Date object set to today
const toTodayDate = (hourTime) => {
    if (!hourTime) return null;
    const [time, modifier] = hourTime.trim().split(" ");
    if (!time) return null;
    let [hours, minutes] = time.split(":").map(Number);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;

    if (modifier?.toUpperCase() === "PM" && hours !== 12) hours += 12;
    if (modifier?.toUpperCase() === "AM" && hours === 12) hours = 0;

    const d = new Date();
    d.setHours(hours, minutes, 0, 0);
    return d;
};

const findNextBell = (bells, now) => {
    const upcoming = (bells || [])
        .map((bell) => ({bell, at: toTodayDate(bell.hourTime)}))
        .filter((x) => x.at && x.at.getTime() > now.getTime())
        .sort((a, b) => a.at - b.at);

    return upcoming[0] || null;
};

const DigitTile = ({children}) => (
    <Flex
        align="center"
        justify="center"
        bg="white"
        color="gray.800"
        borderRadius={{base: "md", md: "xl"}}
        boxShadow="0 8px 20px rgba(0,0,0,0.18)"
        minW={{base: "38px", sm: "46px", md: "56px"}}
        h={{base: "50px", sm: "60px", md: "72px"}}
        px={{base: 1, md: 2}}
        fontWeight="800"
        fontSize={{base: "xl", sm: "3xl", md: "5xl"}}
        lineHeight="1"
        fontFamily="mono"
    >
        {children}
    </Flex>
);

const TimeDisplay = ({hourTime}) => {
    // "10:00 AM" -> ["10", "00", "AM"]
    const [clock, modifier] = (hourTime || "--:-- --").split(" ");
    const [hh, mm] = (clock || "--:--").split(":");

    return (
        <HStack spacing={{base: 1.5, md: 3}} align="center">
            <DigitTile>{hh ?? "--"}</DigitTile>
            <Text fontSize={{base: "2xl", md: "5xl"}} fontWeight="800" color="whiteAlpha.800" lineHeight="1">
                :
            </Text>
            <DigitTile>{mm ?? "--"}</DigitTile>
            {modifier && <DigitTile>{modifier}</DigitTile>}
        </HStack>
    );
};

const formatCountdown = (ms) => {
    if (ms == null || ms < 0) return null;
    const totalSeconds = Math.floor(ms / 1000);
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    const pad = (n) => String(n).padStart(2, "0");
    return h > 0 ? `${h}h ${pad(m)}m ${pad(s)}s` : `${pad(m)}m ${pad(s)}s`;
};

// Animated equalizer used during playback
const Equalizer = () => {
    const bars = [0, 0.15, 0.3, 0.45, 0.6, 0.3, 0.1];
    return (
        <HStack spacing={1.5} align="flex-end" h="40px">
            {bars.map((delay, i) => (
                <Box
                    key={i}
                    w={{base: "5px", md: "7px"}}
                    h="100%"
                    borderRadius="full"
                    bg="white"
                    transformOrigin="bottom"
                    animation={`${bounce} 0.9s ease-in-out ${delay}s infinite`}
                />
            ))}
        </HStack>
    );
};

// The animated ringing-bell badge shown while a bell is playing
const RingingBell = () => (
    <Flex align="center" justify="center" position="relative" w="64px" h="64px" flexShrink={0}>
        {[0, 0.6].map((delay) => (
            <Box
                key={delay}
                position="absolute"
                inset="0"
                borderRadius="full"
                border="2px solid"
                borderColor="whiteAlpha.700"
                animation={`${wave} 1.6s ease-out ${delay}s infinite`}
            />
        ))}
        <Flex
            align="center"
            justify="center"
            w="56px"
            h="56px"
            borderRadius="full"
            bg="whiteAlpha.400"
            zIndex={1}
        >
            <Box as="span" transformOrigin="top center" animation={`${ring} 1s ease-in-out infinite`}>
                <Icon as={FaBell} boxSize={7}/>
            </Box>
        </Flex>
    </Flex>
);

const NextBellHero = ({onSeeSchedule}) => {
    const [bells, setBells] = useState(() => readBellInfo());
    const [now, setNow] = useState(() => new Date());
    const [isPlaying, setIsPlaying] = useState(() => localStorage.getItem("isPlaying") === "true");
    const [currentBellId, setCurrentBellId] = useState(() => Number(localStorage.getItem("currentBellId")));
    const [heroAnimation, setHeroAnimation] = useState(() => isHeroAnimationEnabled());

    const refresh = useCallback(() => setBells(readBellInfo()), []);

    useEffect(() => {
        const id = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(id);
    }, []);

    // Bell schedule updates
    useEffect(() => {
        const onStorage = (e) => {
            if (!e.key || e.key === "bellInfo") refresh();
        };
        window.addEventListener(BELL_INFO_UPDATED_EVENT, refresh);
        window.addEventListener("storage", onStorage);
        return () => {
            window.removeEventListener(BELL_INFO_UPDATED_EVENT, refresh);
            window.removeEventListener("storage", onStorage);
        };
    }, [refresh]);

    // Live playback signals (same-tab custom event + cross-tab storage event)
    useEffect(() => {
        const onCustom = (e) => {
            if (e.detail?.key === "isPlaying") setIsPlaying(String(e.detail.value) === "true");
            if (e.detail?.key === "currentBellId") setCurrentBellId(Number(e.detail.value));
        };
        const onStorage = (e) => {
            if (e.key === "isPlaying") setIsPlaying(localStorage.getItem("isPlaying") === "true");
            if (e.key === "currentBellId") setCurrentBellId(Number(localStorage.getItem("currentBellId")));
        };
        window.addEventListener("bell:localstorage", onCustom);
        window.addEventListener("storage", onStorage);
        return () => {
            window.removeEventListener("bell:localstorage", onCustom);
            window.removeEventListener("storage", onStorage);
        };
    }, []);

    // Hero animation flag — react live to runtime toggles (same tab + other tabs).
    useEffect(() => {
        const onUpdate = (e) => {
            setHeroAnimation(
                e?.detail?.enabled != null ? !!e.detail.enabled : isHeroAnimationEnabled()
            );
        };
        const onStorage = (e) => {
            if (!e.key || e.key === "heroAnimationEnabled") setHeroAnimation(isHeroAnimationEnabled());
        };
        window.addEventListener(HERO_ANIMATION_UPDATED_EVENT, onUpdate);
        window.addEventListener("storage", onStorage);
        return () => {
            window.removeEventListener(HERO_ANIMATION_UPDATED_EVENT, onUpdate);
            window.removeEventListener("storage", onStorage);
        };
    }, []);

    // Recomputed as `now` ticks each second, so the artwork follows the real
    // time of day (morning → daytime → evening → night) without a reload.
    const heroGif = useMemo(() => (heroAnimation ? getHeroGifForTime(now) : null), [heroAnimation, now]);

    const next = useMemo(() => findNextBell(bells, now), [bells, now]);
    const countdown = next ? formatCountdown(next.at.getTime() - now.getTime()) : null;
    const typeMeta = next ? typeBadgeMap[next.bell?.type] : null;

    const playingBell = useMemo(
        () => (isPlaying ? (bells || []).find((b) => b.id === currentBellId) : null),
        [isPlaying, bells, currentBellId]
    );
    const playing = isPlaying && !!playingBell;
    const playingTypeMeta = playingBell ? typeBadgeMap[playingBell.type] : null;

    return (
        <>
        <Box
            position="relative"
            overflow="hidden"
            borderRadius="20px"
            color="white"
            bgImage={
                playing
                    ? "linear-gradient(135deg, #059669 0%, #047857 55%, #065f46 100%)"
                    : heroGif
                    ? `linear-gradient(135deg, rgba(15,23,42,0.45) 0%, rgba(15,23,42,0.15) 55%, rgba(15,23,42,0.45) 100%), url(${heroGif})`
                    : "linear-gradient(135deg, #2563eb 0%, #1e40af 55%, #1e3a8a 100%)"
            }
            bgSize="cover"
            bgPosition="center"
            bgRepeat="no-repeat"
            boxShadow={playing ? "0 18px 40px rgba(5,150,105,0.35)" : "0 18px 40px rgba(30,64,175,0.35)"}
            transition="background-image 0.4s ease, box-shadow 0.4s ease"
            w="100%"
            px={{base: 4, md: 7}}
            py={{base: 4, md: 5}}
            minH={{base: "auto", md: "184px"}}
        >
            {/* Decorative glow circles */}
            <Box
                position="absolute"
                top="-60px"
                right="-40px"
                w="220px"
                h="220px"
                borderRadius="full"
                bg="whiteAlpha.200"
                filter="blur(2px)"
            />
            <Box
                position="absolute"
                bottom="-80px"
                left="-50px"
                w="200px"
                h="200px"
                borderRadius="full"
                bg="whiteAlpha.100"
            />

            <Flex
                position="relative"
                direction={{base: "column", md: "row"}}
                align={{base: "flex-start", md: "center"}}
                justify="space-between"
                gap={5}
                h="100%"
            >
                {playing ? (
                    /* ----------------------- NOW PLAYING ----------------------- */
                    <VStack align="flex-start" spacing={{base: 3, md: 4}} flex="1" minW="0">
                        <HStack spacing={4}>
                            <RingingBell/>
                            <Box>
                                <Text fontSize={{base: "xs", md: "sm"}} color="whiteAlpha.900" letterSpacing="wide">
                                    NOW PLAYING
                                </Text>
                                <Text fontSize={{base: "lg", md: "2xl"}} fontWeight="800" lineHeight="1.1" noOfLines={1}>
                                    {playingBell.name || "Bell ringing"}
                                </Text>
                            </Box>
                        </HStack>

                        <Equalizer/>

                        <HStack spacing={3} flexWrap="wrap">
                            <Badge
                                display="flex"
                                alignItems="center"
                                gap={2}
                                bg="whiteAlpha.300"
                                color="white"
                                borderRadius="full"
                                px={3}
                                py={1.5}
                                fontSize="sm"
                                textTransform="none"
                            >
                                <Icon as={FaRegClock}/>
                                {playingBell.hourTime}
                            </Badge>
                            {playingTypeMeta && (
                                <Badge
                                    bg="whiteAlpha.900"
                                    color="green.700"
                                    borderRadius="full"
                                    px={3}
                                    py={1.5}
                                    fontSize="sm"
                                    textTransform="none"
                                >
                                    {playingTypeMeta.label}
                                </Badge>
                            )}
                        </HStack>

                        <Button
                            size={{base: "sm", md: "md"}}
                            bg="whiteAlpha.900"
                            color="green.700"
                            _hover={{bg: "white"}}
                            _active={{bg: "whiteAlpha.800"}}
                            borderRadius="full"
                            rightIcon={<FiArrowRight/>}
                            onClick={onSeeSchedule}
                        >
                            See Full Schedule
                        </Button>
                    </VStack>
                ) : (
                    /* ------------------------ NEXT BELL ------------------------ */
                    <VStack align="flex-start" spacing={{base: 3, md: 4}} flex="1" minW="0">
                        <HStack spacing={3}>
                            <Flex
                                align="center"
                                justify="center"
                                w="44px"
                                h="44px"
                                borderRadius="full"
                                bg="whiteAlpha.300"
                                position="relative"
                            >
                                <Box
                                    position="absolute"
                                    inset="0"
                                    borderRadius="full"
                                    bg="whiteAlpha.400"
                                    animation={next ? `${pulse} 2s ease-in-out infinite` : undefined}
                                />
                                <Icon as={FaBell} boxSize={5} zIndex={1}/>
                            </Flex>
                            <Box>
                                <Text fontSize={{base: "xs", md: "sm"}} color="whiteAlpha.800" letterSpacing="wide">
                                    {next ? "NEXT BELL" : "NO MORE BELLS TODAY"}
                                </Text>
                                <Text fontSize={{base: "lg", md: "2xl"}} fontWeight="800" lineHeight="1.1">
                                    {next ? `Next Bell: ${next.bell.hourTime}` : "All done for today"}
                                </Text>
                            </Box>
                        </HStack>

                        <TimeDisplay hourTime={next?.bell?.hourTime}/>

                        <HStack spacing={3} flexWrap="wrap">
                            {next && (
                                <Badge
                                    display="flex"
                                    alignItems="center"
                                    gap={2}
                                    bg="whiteAlpha.300"
                                    color="white"
                                    borderRadius="full"
                                    px={3}
                                    py={1.5}
                                    fontSize="sm"
                                    textTransform="none"
                                >
                                    <Icon as={FaRegClock}/>
                                    {countdown ? `Starts in ${countdown}` : "Starting now"}
                                </Badge>
                            )}
                            {typeMeta && (
                                <Badge
                                    bg="whiteAlpha.900"
                                    color="blue.700"
                                    borderRadius="full"
                                    px={3}
                                    py={1.5}
                                    fontSize="sm"
                                    textTransform="none"
                                >
                                    {typeMeta.label}
                                </Badge>
                            )}
                            {next?.bell?.name && (
                                <Text fontSize="sm" color="whiteAlpha.900" noOfLines={1}>
                                    {next.bell.name}
                                </Text>
                            )}
                        </HStack>

                        <Button
                            size={{base: "sm", md: "md"}}
                            bg="whiteAlpha.900"
                            color="blue.700"
                            _hover={{bg: "white"}}
                            _active={{bg: "whiteAlpha.800"}}
                            borderRadius="full"
                            rightIcon={<FiArrowRight/>}
                            onClick={onSeeSchedule}
                        >
                            See Full Schedule
                        </Button>
                    </VStack>
                )}

                {/* Live clock + quick context */}
                <VStack
                    align={{base: "flex-start", md: "flex-end"}}
                    spacing={1}
                    color="whiteAlpha.900"
                    minW={{md: "150px"}}
                >
                    <Text fontSize={{base: "md", md: "xl"}} fontWeight="700" fontFamily="mono">
                        {now.toLocaleTimeString([], {hour: "2-digit", minute: "2-digit", second: "2-digit"})}
                    </Text>
                    <Text fontSize="xs" color="whiteAlpha.700">
                        {now.toLocaleDateString([], {weekday: "long", month: "short", day: "numeric"})}
                    </Text>
                </VStack>
            </Flex>
        </Box>

        {/* Marquee ticker at the bottom of the hero — comment out to hide. */}
        <Suspense fallback={null}>
            <MarqueeSlider mt={{base: 4, md: 5}}/>
        </Suspense>
        </>
    );
};

export default NextBellHero;
